import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

const CsvGuideModal = ({ isOpen, onClose, onImportFile, fileInputRef }) => {
  // Add event listener for Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (isOpen && e.key === 'Escape') {
        onClose();
      }
    };
    
    // Add event listener
    document.addEventListener('keydown', handleEsc);
    
    // Clean up on unmount
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center"
      onClick={onClose}
      tabIndex="0"
    >
      <div 
        className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 md:mx-0 p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">CSV Import Format Guide</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="mb-6 max-h-[70vh] overflow-y-auto pr-2">
          <p className="text-sm text-gray-600 mb-4">
            To import leads, please format your CSV file according to the following guidelines:
          </p>
          
          <div>
            <h4 className="font-medium text-gray-800 mb-2">Field Descriptions</h4>
            <p className="text-sm text-gray-600 mb-2">
              Your CSV file should include appropriate column headers in the first row. Only <strong>Email</strong> is required. Name fields (<strong>Name</strong>, <strong>First Name</strong>, <strong>Last Name</strong>) are recommended but optional.
            </p>
            <div className="overflow-x-auto border border-gray-200 rounded-md mb-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Required</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 text-sm">
                  <tr>
                    <td className="px-3 py-2 font-mono">Email</td>
                    <td className="px-3 py-2">Email address</td>
                    <td className="px-3 py-2 text-green-600">Yes</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">First Name</td>
                    <td className="px-3 py-2">First name of the lead</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Last Name</td>
                    <td className="px-3 py-2">Last name/surname of the lead</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Name</td>
                    <td className="px-3 py-2">Full name of the lead</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Phone</td>
                    <td className="px-3 py-2">Phone number</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Original Source</td>
                    <td className="px-3 py-2">Original lead acquisition source (defaults to "CSV Import" if empty)</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Latest Source</td>
                    <td className="px-3 py-2">Most recent marketing touchpoint (defaults to same as Original Source if empty)</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Status</td>
                    <td className="px-3 py-2">Lead status (New, Contacted, Qualified, Converted, Lost)</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Notes</td>
                    <td className="px-3 py-2">Additional information about the lead</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Country</td>
                    <td className="px-3 py-2">Lead's country</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Assigned To</td>
                    <td className="px-3 py-2">User ID to assign the lead to</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Call Attempts</td>
                    <td className="px-3 py-2">Number of call attempts (0-10)</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Subscribed</td>
                    <td className="px-3 py-2">Whether the lead is subscribed to marketing emails (yes/no/true/false/1/0/y/n/on/off). Case-insensitive. Will be TRUE if empty.</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Created At</td>
                    <td className="px-3 py-2">Date when the lead was created (YYYY-MM-DD format). If not provided, current date is used.</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Last Worked Date</td>
                    <td className="px-3 py-2">Date when the lead was last worked on (YYYY-MM-DD format). Used for tracking agent activity.</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 font-mono">Reconversion Date</td>
                    <td className="px-3 py-2">Date when the lead was reconverted (YYYY-MM-DD format). Used for tracking lead reconversions.</td>
                    <td className="px-3 py-2 text-red-600">No</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="grid grid-cols-1 gap-4 mb-4">
            <div className="bg-gray-50 rounded-md p-4">
              <h4 className="font-medium text-gray-800 mb-2">Example CSV Content</h4>
              <div className="bg-white p-3 rounded border border-gray-200 overflow-x-auto">
                <pre className="text-xs font-mono">
First Name,Last Name,Email,Phone,Original Source,Latest Source,Status,Notes,Country,Assigned To,Call Attempts,Subscribed,Created At,Last Worked Date,Reconversion Date
John,Doe,john.doe@example.com,555-123-4567,Website,Website,New,"Interested in tutoring",USA,,0,yes,2025-03-20,2025-03-25,
Jane,Smith,jane.smith@example.com,555-987-6543,Referral,Social Media,Contacted,"Follow up next week",Canada,,2,no,2025-03-25,2025-04-03,2025-04-05
"Michael Lee",,"mike@example.com",555-555-5555,CSV Import,Email Campaign,HOT,"Urgent follow-up",UK,,1,yes,2025-03-30,,
,,"minimal@example.com",,,,,,,,,,,,
</pre>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="bg-blue-50 border border-blue-100 rounded-md p-4 h-full">
                <h4 className="font-medium text-blue-800 flex items-center mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Tips
                </h4>
                <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
                  <li>Most spreadsheet applications can export data in CSV format</li>
                  <li>Ensure that text with commas is enclosed in double quotes</li>
                  <li>Column headers are case-insensitive (e.g., "name", "Name", or "NAME" will work)</li>
                  <li>Alternative column names are also supported (e.g., "E-Mail" for "Email")</li>
                  <li>If name fields are missing, the system will try to extract them from each other, or use the portion of email before '@' as a fallback</li>
                  <li>When in doubt, use a spreadsheet application to export to CSV</li>
                  <li>Maximum file size: 5MB</li>
                  <li>The system will attempt to import all rows, even if some fail</li>
                </ul>
              </div>
            </div>
            
            <div>
              <div className="bg-green-50 border border-green-100 rounded-md p-4 h-full">
                <h4 className="font-medium text-green-800 flex items-center mb-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Existing Leads
                </h4>
                <p className="text-sm text-gray-600 mb-2">
                  When importing a lead with an email that already exists in the system:
                </p>
                <ul className="text-sm text-gray-600 list-disc pl-5 space-y-1">
                  <li>The system will update the existing lead rather than creating a duplicate</li>
                  <li>Any fields not included in the CSV will retain their existing values</li>
                  <li>Notes from the CSV will be appended to existing notes with a timestamp</li>
                  <li>The import results will show both new leads created and existing leads updated</li>
                </ul>
              </div>
            </div>
          </div>
          
        </div>
        
        <div className="flex justify-between">
          <button
            onClick={() => {
              // Trigger the file input
              if (fileInputRef && fileInputRef.current) {
                fileInputRef.current.click();
              }
              onClose();
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700"
          >
            Choose CSV File to Import
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-md shadow-sm hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

CsvGuideModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImportFile: PropTypes.func,
  fileInputRef: PropTypes.object
};

export default CsvGuideModal;